import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Sun, Moon, Bell, Command, MessageCircle, Menu, Check, X as XIcon, CheckCheck, Loader2 } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Topbar({ onOpenSearch, onOpenMobileNav }) {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [merchReqs, setMerchReqs] = useState([]); // admin only: to resolve merch-request state
  const [notifOpen, setNotifOpen] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set()); // notification ids currently mid-request
  const [markingAll, setMarkingAll] = useState(false);
  const panelRef = useRef(null);
  const isAdmin = user && user.role === "admin";

  const loadNotifs = useCallback(async () => {
    try {
      const [{ data: n }, admin] = await Promise.all([
        api.get("/notifications"),
        isAdmin ? api.get("/admin/merch-requests").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setNotifs(n);
      setMerchReqs(admin.data || []);
    } catch {
      /* silent */
    }
  }, [isAdmin]);

  useEffect(() => {
    loadNotifs();
    // Poll every 30s so admins see new merch requests without a manual refresh.
    const id = setInterval(loadNotifs, 30_000);
    const onFocus = () => loadNotifs();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadNotifs]);

  useEffect(() => {
    if (!notifOpen) return;
    function onDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setNotifOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setNotifOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [notifOpen]);

  const unread = notifs.filter((n) => !n.read).length;
  const merchByReqId = new Map(merchReqs.map((r) => [r.id, r]));

  // A merch-request notification is "actionable" only if the linked request is still pending.
  function pendingRequestFor(n) {
    if (n.category !== "merch" || !n.merch_request_id) return null;
    const r = merchByReqId.get(n.merch_request_id);
    return r && r.status === "pending" ? r : null;
  }

  const hasPendingMerchRequest = isAdmin && notifs.some((n) => pendingRequestFor(n));

  async function markOneRead(nid) {
    // Optimistic
    setNotifs((prev) => prev.map((x) => (x.id === nid ? { ...x, read: true } : x)));
    api.post(`/notifications/${nid}/read`).catch(() => {});
  }

  async function actOnRequest(n, status) {
    const req = pendingRequestFor(n);
    if (!req) return;
    setBusyIds((prev) => new Set(prev).add(n.id));
    try {
      const { data } = await api.patch(`/admin/merch-requests/${req.id}`, { status });
      setMerchReqs((prev) => prev.map((r) => (r.id === req.id ? data : r)));
      // Optimistically mark this notification read since backend clears related ones on decisions.
      setNotifs((prev) => prev.map((x) => (x.merch_request_id === req.id ? { ...x, read: true } : x)));
      toast.success(status === "approved" ? "Request approved" : "Request declined");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.delete(n.id);
      return next;
    });
  }

  async function markAllRead() {
    if (hasPendingMerchRequest) {
      toast.info("Resolve pending merch requests before clearing.");
      return;
    }
    setMarkingAll(true);
    // Optimistic
    setNotifs((prev) => prev.map((x) => ({ ...x, read: true })));
    try {
      await api.post("/notifications/read-all");
    } catch (err) {
      toast.error(formatApiError(err));
      loadNotifs();
    }
    setMarkingAll(false);
  }

  return (
    <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-3.5">
        {onOpenMobileNav && (
          <button
            onClick={onOpenMobileNav}
            className="md:hidden p-2 rounded-sm hover:bg-surface-elev text-muted-foreground hover:text-foreground transition"
            data-testid="topbar-mobile-nav"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        <button
          onClick={onOpenSearch}
          className="flex-1 max-w-xl flex items-center gap-3 px-3 sm:px-4 py-2 rounded-sm border border-border bg-surface hover:border-border-strong transition group min-w-0"
          data-testid="topbar-search-trigger"
        >
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground flex-1 text-left truncate">Search…</span>
          <span className="hidden sm:flex items-center gap-1 font-mono text-[10px] tracking-widest text-muted-foreground shrink-0">
            <Command className="h-3 w-3" /> K
          </span>
        </button>

        <div className="flex items-center gap-1">
          <button
            onClick={() => nav("/assistant")}
            className="flex items-center gap-2 px-3 py-2 rounded-sm border border-border bg-surface hover:border-border-strong hover:bg-surface-elev text-foreground transition"
            data-testid="topbar-assistant"
            aria-label="AI Assistant"
          >
            <MessageCircle className="h-4 w-4" style={{ color: "#FBC90C" }} />
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.3em]">AI Assistant</span>
          </button>

          <button
            onClick={toggle}
            className="p-2 rounded-sm hover:bg-surface-elev text-muted-foreground hover:text-foreground transition"
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <div className="relative" ref={panelRef}>
            <button
              onClick={() => setNotifOpen((v) => !v)}
              className="p-2 rounded-sm hover:bg-surface-elev text-muted-foreground hover:text-foreground transition relative"
              data-testid="topbar-notifications"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#E15C2C] text-white font-mono text-[9px] font-bold flex items-center justify-center" data-testid="notif-badge">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            {notifOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-1.5rem)] rounded-sm border shadow-2xl spotlight-scale-in overflow-hidden isolate"
                style={{ background: "hsl(var(--surface))", borderColor: "hsl(var(--border-strong))" }}
                data-testid="notif-panel"
              >
                <div className="px-4 py-3 border-b border-border flex items-center justify-between" style={{ background: "hsl(var(--surface-elev))" }}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Notifications</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground" data-testid="notif-unread-count">{unread} unread</span>
                    <button
                      onClick={markAllRead}
                      disabled={unread === 0 || markingAll || hasPendingMerchRequest}
                      className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition"
                      data-testid="notif-mark-all-read"
                      title={hasPendingMerchRequest ? "Resolve pending merch requests first" : "Mark all as read"}
                    >
                      {markingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />}
                      Clear
                    </button>
                  </div>
                </div>
                <div className="max-h-[440px] overflow-y-auto" style={{ background: "hsl(var(--surface))" }}>
                  {notifs.length === 0 && (
                    <div className="p-8 text-sm text-muted-foreground text-center">All caught up.</div>
                  )}
                  {notifs.map((n) => {
                    const pending = pendingRequestFor(n);
                    const busy = busyIds.has(n.id);
                    return (
                      <div
                        key={n.id}
                        className={`px-4 py-3 border-b border-border last:border-b-0 transition ${n.read ? "opacity-60" : ""}`}
                        data-testid={`notif-row-${n.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            markOneRead(n.id);
                            if (n.link) {
                              setNotifOpen(false);
                              nav(n.link);
                            }
                          }}
                          className="w-full text-left hover:opacity-90"
                          data-testid={`notif-open-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.read && (
                              <span className="mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full bg-[#E15C2C]" aria-hidden />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold">{n.title}</div>
                              <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{n.category}</div>
                            </div>
                          </div>
                        </button>
                        {isAdmin && pending && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border" data-testid={`notif-actions-${n.id}`}>
                            <button
                              disabled={busy}
                              onClick={() => actOnRequest(n, "approved")}
                              className="flex-1 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm text-black flex items-center justify-center gap-1 disabled:opacity-50"
                              style={{ background: "#FBC90C" }}
                              data-testid={`notif-approve-${n.id}`}
                            >
                              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                              Approve
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => actOnRequest(n, "declined")}
                              className="flex-1 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm text-white flex items-center justify-center gap-1 disabled:opacity-50"
                              style={{ background: "#E15C2C" }}
                              data-testid={`notif-decline-${n.id}`}
                            >
                              <XIcon className="h-3 w-3" />
                              Decline
                            </button>
                            <button
                              onClick={() => {
                                setNotifOpen(false);
                                nav("/admin#merch");
                              }}
                              className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-border text-muted-foreground hover:text-foreground hover:border-border-strong"
                              data-testid={`notif-open-admin-${n.id}`}
                              title="Open in Admin"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
